import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, User, MapPin, Car, PhoneCall, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { TelecallerService } from '../../services/telecallerService';
import { maskPhoneLast4 } from '../../utils/phoneMask';
import { parseCallGatewayResult, shouldBlockTap, makeCoolDownState } from '../../utils/callGateway';
import CallStatusBadge from '../../components/CallStatusBadge';

const NotAnswer = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [callNotes, setCallNotes] = useState('');
  const [callStatus, setCallStatus] = useState('not_answer');
  const [isLogging, setIsLogging] = useState(false);
  const [callStateByLeadId, setCallStateByLeadId] = useState({});

  const { data: leadsData, isLoading, refetch } = useQuery({
    queryKey: ['telecallerNotAnswerLeads', user?.id],
    queryFn: () => TelecallerService.getNotAnswerLeads(user?.id),
    enabled: !!user?.id,
  });

  const leads = leadsData?.data || [];

  const handleCall = (lead) => {
    setSelectedLead(lead);
    setShowCallModal(true);
  };

  const handleEndCall = async () => {
    if (!selectedLead || !callStatus) {
      alert('Please select call status');
      return;
    }

    setIsLogging(true);
    try {
      const result = await TelecallerService.logCall(
        selectedLead.id,
        user?.id,
        callStatus,
        callNotes
      );

      if (result.success) {
        alert('Call logged successfully!');
        setShowCallModal(false);
        setSelectedLead(null);
        setCallNotes('');
        setCallStatus('');
        refetch();
      } else {
        alert('Failed to log call: ' + result.error);
      }
    } catch (error) {
      alert('Error logging call: ' + error.message);
    } finally {
      setIsLogging(false);
    }
  };

  const callCustomer = (leadId, phoneNumber) => {
    const k = String(leadId);
    if (shouldBlockTap(callStateByLeadId[k])) return;

    setCallStateByLeadId((m) => ({
      ...m,
      [k]: { status: 'calling', statusText: 'calling', reason: '', lockUntilMs: Date.now() + 4000 },
    }));

    TelecallerService.sarvClickToCall(user?.phone || user?.contact_number, phoneNumber, {
      lead_id: leadId,
      telecaller_id: user?.id,
    })
      .then((res) => {
        const parsed = parseCallGatewayResult(res);
        setCallStateByLeadId((m) => ({
          ...m,
          [k]: makeCoolDownState(parsed, parsed.ok ? 4000 : 2000),
        }));
      })
      .catch((e) => {
        setCallStateByLeadId((m) => ({
          ...m,
          [k]: makeCoolDownState(
            {
              status: 'failed',
              statusText: 'failed',
              reason: String(e?.message || e),
              providerLabel: '',
              attempts: [],
            },
            2000
          ),
        }));
      });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/telecaller/dashboard')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Not Answer Leads</h1>
          <p className="text-gray-600 mt-1">Leads that did not answer your calls</p>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading leads...</p>
        </div>
      ) : leads.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No "Not Answer" leads</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leads.map((lead) => (
            <div key={lead.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <User size={24} className="text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{lead.name || 'Unknown'}</h3>
                    <p className="text-sm text-gray-600">{maskPhoneLast4(lead.contact_number) || 'N/A'}</p>
                  </div>
                </div>
                <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700">
                  Not Answer
                </span>
              </div>

              <div className="space-y-2 text-sm mb-4">
                {lead.address && (
                  <div className="flex items-start space-x-2">
                    <MapPin size={16} className="text-gray-400 mt-0.5" />
                    <span className="text-gray-600">{lead.address}</span>
                  </div>
                )}
                {lead.car_model && (
                  <div className="flex items-center space-x-2">
                    <Car size={16} className="text-gray-400" />
                    <span className="text-gray-600">{lead.car_model}</span>
                  </div>
                )}
              </div>

              {(() => {
                const st = callStateByLeadId[String(lead.id)];
                const isCalling = st?.status === 'calling';
                const isCoolingDown = st && shouldBlockTap(st);
                return (
                  <>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => callCustomer(lead.id, lead.contact_number)}
                        disabled={isCoolingDown}
                        className={`flex-1 px-3 py-2 rounded-lg flex items-center justify-center space-x-1 text-white ${
                          isCoolingDown
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-green-500 hover:bg-green-600'
                        }`}
                      >
                        <Phone size={16} className={isCalling ? 'animate-pulse' : ''} />
                        <span>{isCalling ? 'Calling...' : 'Call'}</span>
                      </button>
                      <button
                        onClick={() => handleCall(lead)}
                        className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center space-x-1"
                      >
                        <PhoneCall size={16} />
                        <span>Log</span>
                      </button>
                    </div>

                    <CallStatusBadge state={st} />
                  </>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* Call Modal */}
      {showCallModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Log Call</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Customer</p>
                <p className="font-semibold text-gray-900">{selectedLead.name || 'Unknown'}</p>
                <p className="text-sm text-gray-600">{maskPhoneLast4(selectedLead.contact_number) || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Call Status *</label>
                <select
                  value={callStatus}
                  onChange={(e) => setCallStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="not_answer">Not Answer</option>
                  <option value="answered">Answered</option>
                  <option value="callback">Callback</option>
                  <option value="not_interested">Not Interested</option>
                  <option value="confirmed">Confirmed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Call Notes</label>
                <textarea
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Add call notes..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowCallModal(false);
                    setSelectedLead(null);
                    setCallNotes('');
                    setCallStatus('not_answer');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEndCall}
                  disabled={!callStatus || isLogging}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                >
                  {isLogging ? 'Logging...' : 'Log Call'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotAnswer;

